import React, { useEffect, useMemo, useRef, useState } from "react";
import { Filter, X, Check, ChevronDown } from "lucide-react";
import http from "@/lib/http";

/**
 * Shared filter chips rendered above every lead overview (Leads /
 * Particulieren / Parkeren / StatusView). Four orthogonal filters:
 *   • Status (multi-select, scoped to source_type when provided)
 *   • Live-status (online/offline)
 *   • In optie (ja/nee)
 *   • Prijs (min ... max, optioneel)
 *
 * The current selection is mirrored to URL search params so deep-links and
 * back/forward navigation just work. Parent passes `value` + `onChange`:
 *   value   = { status: [...], live_status: [...], in_option: true|false,
 *               price_min: number|null, price_max: number|null }
 *   onChange = (next) => void   // partial merge, key removed when value is null
 *
 * `sourceType`:
 *   • "makelaar" / "particulier"   → only show statuses for that source
 *   • "all" or undefined           → show both source-type status sets
 */
export default function LeadFilterChips({ value, onChange, sourceType, hideStatus = false, actionFilters = null }) {
  const [statuses, setStatuses] = useState([]);
  const [open, setOpen] = useState(null); // null | "status" | "live" | "option" | "price" | "actions"

  useEffect(() => {
    const params = {};
    if (sourceType && sourceType !== "all") params.source_type = sourceType;
    http.get("/api/lead-statuses", { params })
      .then(r => setStatuses(r.data || []))
      .catch(() => setStatuses([]));
  }, [sourceType]);

  // Deduplicate statuses by key (when no source filter is active, both
  // makelaar + particulier rows can share the same key like "new").
  const statusOptions = useMemo(() => {
    const seen = new Map();
    for (const s of statuses) {
      if (!seen.has(s.key)) seen.set(s.key, { key: s.key, label: s.label, color: s.color });
    }
    return Array.from(seen.values());
  }, [statuses]);

  const selectedStatus = value.status || [];
  const selectedLive = value.live_status || [];
  const inOption = value.in_option;
  const priceMin = value.price_min ?? null;
  const priceMax = value.price_max ?? null;

  const toggle = (key, listKey) => {
    const cur = value[listKey] || [];
    const next = cur.includes(key) ? cur.filter(x => x !== key) : [...cur, key];
    onChange({ ...value, [listKey]: next.length ? next : null });
  };
  const setInOption = (v) => onChange({ ...value, in_option: v });
  const setPrice = (next) => onChange({ ...value, ...next });
  /** Set action_state[key] to "done" | "not_done" | null (3-way toggle). */
  const setActionState = (key, state) => {
    const cur = value.action_state || {};
    const next = { ...cur };
    if (state === null) delete next[key]; else next[key] = state;
    onChange({ ...value, action_state: Object.keys(next).length ? next : null });
  };
  const clearAll = () => onChange({
    status: null, live_status: null, in_option: null,
    price_min: null, price_max: null, action_state: null,
  });

  const priceActive = priceMin !== null || priceMax !== null;
  const actionState = value.action_state || {};
  const actionActiveCount = Object.keys(actionState).length;
  const activeCount =
    (selectedStatus.length ? 1 : 0) +
    (selectedLive.length ? 1 : 0) +
    (inOption !== null && inOption !== undefined ? 1 : 0) +
    (priceActive ? 1 : 0) +
    (actionActiveCount ? 1 : 0);

  const summary = (label, count) => count
    ? <span className="ml-1 text-[10px] bg-[var(--teal-700)] text-white rounded-full px-1.5 py-px tabular">{count}</span>
    : null;

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest?.("[data-filter-chip]")) setOpen(null);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="lead-filter-chips">
      <div className="flex items-center gap-1 text-[12px] text-[var(--ink-500)] mr-1">
        <Filter size={13} /> Filter:
      </div>

      {!hideStatus && (
        <ChipDropdown
          testid="filter-chip-status"
          label="Status"
          summary={summary("Status", selectedStatus.length)}
          open={open === "status"}
          onToggle={() => setOpen(open === "status" ? null : "status")}>
          {statusOptions.length === 0 && (
            <div className="px-3 py-2 text-[12px] text-[var(--ink-400)]">Geen statussen</div>
          )}
          {statusOptions.map(s => {
            const on = selectedStatus.includes(s.key);
            return (
              <button key={s.key} type="button"
                      data-testid={`filter-status-${s.key}`}
                      onClick={() => toggle(s.key, "status")}
                      className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center justify-between
                                  hover:bg-[var(--sand-100)] ${on ? "bg-[var(--teal-50)]" : ""}`}>
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full chip-${s.color || "slate"}`} />
                  {s.label}
                </span>
                {on && <Check size={13} className="text-[var(--teal-700)]" />}
              </button>
            );
          })}
        </ChipDropdown>
      )}

      <ChipDropdown
        testid="filter-chip-live"
        label="Online status"
        summary={summary("Live", selectedLive.length)}
        open={open === "live"}
        onToggle={() => setOpen(open === "live" ? null : "live")}>
        {[
          { k: "active", label: "Online" },
          { k: "offline", label: "Offline" },
        ].map(o => {
          const on = selectedLive.includes(o.k);
          return (
            <button key={o.k} type="button"
                    data-testid={`filter-live-${o.k}`}
                    onClick={() => toggle(o.k, "live_status")}
                    className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center justify-between
                                hover:bg-[var(--sand-100)] ${on ? "bg-[var(--teal-50)]" : ""}`}>
              <span>{o.label}</span>
              {on && <Check size={13} className="text-[var(--teal-700)]" />}
            </button>
          );
        })}
      </ChipDropdown>

      <ChipDropdown
        testid="filter-chip-option"
        label="In optie"
        summary={inOption !== null && inOption !== undefined
          ? <span className="ml-1 text-[10px] bg-[var(--teal-700)] text-white rounded-full px-1.5 py-px">{inOption ? "Ja" : "Nee"}</span>
          : null}
        open={open === "option"}
        onToggle={() => setOpen(open === "option" ? null : "option")}>
        {[
          { v: true, label: "Ja, in optie" },
          { v: false, label: "Nee, niet in optie" },
        ].map(o => {
          const on = inOption === o.v;
          return (
            <button key={String(o.v)} type="button"
                    data-testid={`filter-option-${o.v ? "yes" : "no"}`}
                    onClick={() => setInOption(on ? null : o.v)}
                    className={`w-full text-left px-3 py-1.5 text-[13px] flex items-center justify-between
                                hover:bg-[var(--sand-100)] ${on ? "bg-[var(--teal-50)]" : ""}`}>
              <span>{o.label}</span>
              {on && <Check size={13} className="text-[var(--teal-700)]" />}
            </button>
          );
        })}
      </ChipDropdown>

      <PriceChip
        priceMin={priceMin}
        priceMax={priceMax}
        active={priceActive}
        open={open === "price"}
        onToggle={() => setOpen(open === "price" ? null : "price")}
        onApply={setPrice}
      />

      {actionFilters && actionFilters.length > 0 && (
        <ChipDropdown
          testid="filter-chip-actions"
          label={actionFilters.length === 1 ? actionFilters[0].label : "Brieven"}
          summary={summary("Acties", actionActiveCount)}
          open={open === "actions"}
          onToggle={() => setOpen(open === "actions" ? null : "actions")}>
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--ink-500)] border-b border-[var(--border-soft)]">
            Filter op uitgevoerde acties
          </div>
          {actionFilters.map(af => {
            const state = actionState[af.key] || null; // null | "done" | "not_done"
            return (
              <div key={af.key} className="px-3 py-2 border-b border-[var(--border-soft)] last:border-b-0">
                <div className="text-[12px] font-medium text-[var(--ink-700)] mb-1.5">{af.label}</div>
                <div className="flex gap-1">
                  {[
                    { k: null,       label: "Alles" },
                    { k: "done",     label: "Verstuurd" },
                    { k: "not_done", label: "Niet verstuurd" },
                  ].map(o => {
                    const on = state === o.k;
                    return (
                      <button key={String(o.k)} type="button"
                              data-testid={`filter-action-${af.key}-${o.k || "all"}`}
                              onClick={() => setActionState(af.key, o.k)}
                              className={`px-2.5 h-7 rounded-md text-[11px] font-medium transition border
                                ${on ? "bg-[var(--teal-700)] text-white border-[var(--teal-700)]"
                                     : "bg-white text-[var(--ink-700)] border-[var(--border-soft)] hover:border-[var(--border-strong)]"}`}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </ChipDropdown>
      )}

      {activeCount > 0 && (
        <button type="button" onClick={clearAll}
                data-testid="filter-clear-all"
                className="flex items-center gap-1 text-[12px] text-[var(--ink-500)] hover:text-[var(--danger)] ml-1 px-2 py-1">
          <X size={12} /> Wis ({activeCount})
        </button>
      )}
    </div>
  );
}

function ChipDropdown({ label, summary, open, onToggle, children, testid }) {
  return (
    <div className="relative" data-filter-chip>
      <button type="button" onClick={onToggle}
              data-testid={testid}
              className={`flex items-center gap-1 px-3 h-8 rounded-full text-[12px] font-medium
                         border transition
                         ${open
                           ? "bg-[var(--teal-50)] border-[var(--teal-500)] text-[var(--teal-700)]"
                           : "bg-white border-[var(--border-soft)] hover:border-[var(--border-strong)] text-[var(--ink-700)]"}`}>
        {label} {summary}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 min-w-[200px] max-h-64 overflow-auto
                        bg-white border border-[var(--border-soft)] rounded-lg shadow-lg py-1"
             data-testid={`${testid}-menu`}>
          {children}
        </div>
      )}
    </div>
  );
}

function formatEuro(v) {
  if (v === null || v === undefined || v === "") return "—";
  return "€ " + Number(v).toLocaleString("nl-BE");
}

function PriceChip({ priceMin, priceMax, active, open, onToggle, onApply }) {
  const [minStr, setMinStr] = useState(priceMin !== null ? String(priceMin) : "");
  const [maxStr, setMaxStr] = useState(priceMax !== null ? String(priceMax) : "");
  const lastOpen = useRef(open);

  // Keep local input strings in sync wanneer parent (URL deep-link) verandert.
  useEffect(() => {
    setMinStr(priceMin !== null ? String(priceMin) : "");
    setMaxStr(priceMax !== null ? String(priceMax) : "");
  }, [priceMin, priceMax]);

  const parseAmount = (s) => {
    if (s === null || s === undefined) return null;
    const cleaned = String(s).replace(/[^\d]/g, "");
    if (!cleaned) return null;
    const n = parseInt(cleaned, 10);
    return Number.isFinite(n) ? n : null;
  };

  const apply = () => {
    onApply({ price_min: parseAmount(minStr), price_max: parseAmount(maxStr) });
    onToggle();
  };
  const reset = () => {
    setMinStr(""); setMaxStr("");
    onApply({ price_min: null, price_max: null });
  };

  // Sluit het paneel zonder te verliezen wat de gebruiker tikte
  useEffect(() => {
    if (lastOpen.current && !open) {
      const nextMin = parseAmount(minStr);
      const nextMax = parseAmount(maxStr);
      if (nextMin !== priceMin || nextMax !== priceMax) {
        onApply({ price_min: nextMin, price_max: nextMax });
      }
    }
    lastOpen.current = open;
  }, [open, minStr, maxStr, priceMin, priceMax, onApply]);

  const summary = active
    ? (
      <span className="ml-1 text-[10px] bg-[var(--teal-700)] text-white rounded-full px-1.5 py-px tabular">
        {priceMin !== null ? formatEuro(priceMin) : "—"} – {priceMax !== null ? formatEuro(priceMax) : "—"}
      </span>
    )
    : null;

  return (
    <div className="relative" data-filter-chip>
      <button type="button" onClick={onToggle}
              data-testid="filter-chip-price"
              className={`flex items-center gap-1 px-3 h-8 rounded-full text-[12px] font-medium
                         border transition
                         ${open
                           ? "bg-[var(--teal-50)] border-[var(--teal-500)] text-[var(--teal-700)]"
                           : "bg-white border-[var(--border-soft)] hover:border-[var(--border-strong)] text-[var(--ink-700)]"}`}>
        Prijs {summary}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 w-[280px]
                        bg-white border border-[var(--border-soft)] rounded-lg shadow-lg p-3"
             data-testid="filter-chip-price-menu"
             onClick={(e) => e.stopPropagation()}>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-500)] mb-2">
            Prijsbereik
          </div>
          <div className="flex items-center gap-2">
            <label className="flex-1">
              <span className="block text-[10px] text-[var(--ink-500)] mb-1">Min</span>
              <input
                type="text"
                inputMode="numeric"
                value={minStr}
                onChange={(e) => setMinStr(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                placeholder="0"
                data-testid="filter-price-min"
                className="w-full h-9 px-2 rounded-md border border-[var(--border-strong)] text-[13px] tabular focus:outline-none focus:border-[var(--teal-500)]"
              />
            </label>
            <span className="text-[var(--ink-400)] pt-4">–</span>
            <label className="flex-1">
              <span className="block text-[10px] text-[var(--ink-500)] mb-1">Max</span>
              <input
                type="text"
                inputMode="numeric"
                value={maxStr}
                onChange={(e) => setMaxStr(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                placeholder="∞"
                data-testid="filter-price-max"
                className="w-full h-9 px-2 rounded-md border border-[var(--border-strong)] text-[13px] tabular focus:outline-none focus:border-[var(--teal-500)]"
              />
            </label>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3">
            <button type="button" onClick={reset}
                    data-testid="filter-price-reset"
                    className="text-[12px] text-[var(--ink-500)] hover:text-[var(--danger)]">
              Wissen
            </button>
            <button type="button" onClick={apply}
                    data-testid="filter-price-apply"
                    className="btn btn-primary !h-8 !px-3 !text-[12px]">
              Toepassen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Read filter state from URL params. Centralised so all overview pages share
 * exactly the same parsing.
 */
export function readFilterFromParams(params) {
  const raw = {
    status: (params.get("f_status") || "").split(",").filter(Boolean),
    live_status: (params.get("f_live") || "").split(",").filter(Boolean),
  };
  const opt = params.get("f_option");
  raw.in_option = opt === "true" ? true : opt === "false" ? false : null;
  const pmin = params.get("f_pmin");
  const pmax = params.get("f_pmax");
  raw.price_min = pmin !== null && pmin !== "" ? Number(pmin) : null;
  raw.price_max = pmax !== null && pmax !== "" ? Number(pmax) : null;
  if (!Number.isFinite(raw.price_min)) raw.price_min = null;
  if (!Number.isFinite(raw.price_max)) raw.price_max = null;
  if (!raw.status.length) raw.status = null;
  if (!raw.live_status.length) raw.live_status = null;
  // Action state: f_action=key1:done,key2:not_done
  const rawA = params.get("f_action");
  if (rawA) {
    const parsed = {};
    for (const part of rawA.split(",")) {
      const [k, v] = part.split(":");
      if (k && (v === "done" || v === "not_done")) parsed[k] = v;
    }
    raw.action_state = Object.keys(parsed).length ? parsed : null;
  } else {
    raw.action_state = null;
  }
  return raw;
}

/** Convert filter state → query-string entries to write into URL. */
export function writeFilterToParams(value, params) {
  const next = new URLSearchParams(params);
  next.delete("f_status"); next.delete("f_live"); next.delete("f_option");
  next.delete("f_pmin"); next.delete("f_pmax"); next.delete("f_action");
  if (value.status?.length) next.set("f_status", value.status.join(","));
  if (value.live_status?.length) next.set("f_live", value.live_status.join(","));
  if (value.in_option === true) next.set("f_option", "true");
  if (value.in_option === false) next.set("f_option", "false");
  if (value.price_min !== null && value.price_min !== undefined) next.set("f_pmin", String(value.price_min));
  if (value.price_max !== null && value.price_max !== undefined) next.set("f_pmax", String(value.price_max));
  if (value.action_state && Object.keys(value.action_state).length) {
    next.set("f_action",
      Object.entries(value.action_state).map(([k, v]) => `${k}:${v}`).join(","));
  }
  return next;
}

/** Convert filter state → API query params for GET /api/leads. */
export function filterToApiParams(value) {
  const p = {};
  if (value.status?.length) p.status = value.status.join(",");
  if (value.live_status?.length) p.live_status = value.live_status.join(",");
  if (value.in_option === true || value.in_option === false) p.in_option = value.in_option;
  if (value.price_min !== null && value.price_min !== undefined) p.price_min = value.price_min;
  if (value.price_max !== null && value.price_max !== undefined) p.price_max = value.price_max;
  if (value.action_state) {
    const done = [], notDone = [];
    for (const [k, v] of Object.entries(value.action_state)) {
      if (v === "done") done.push(k);
      else if (v === "not_done") notDone.push(k);
    }
    if (done.length) p.action_done = done.join(",");
    if (notDone.length) p.action_not_done = notDone.join(",");
  }
  return p;
}
